import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../../util/Toast';
import { useSettings } from '../util/SettingsProvider';
export const OfferDetailsPage = () => {
  const { offerId } = useParams();
  const navigate = useNavigate();

  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedTracking, setCopiedTracking] = useState(false);
  const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false);
  // Modal Event Payout State
  const [events, setEvents] = useState([]);
  const [modalSuccess, setModalSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const settings = useSettings();
  useEffect(() => { document.title = `Offer | ${settings?.settings?.site_name || ''}`; }, [settings?.settings?.site_name]);
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  useEffect(() => {
    // Check if offerId is missing or empty, and redirect if so
    if (!offerId || offerId.trim() === '') {
      navigate('/offers', { replace: true });
      return;
    }

    const fetchOfferDetails = async () => {
      try {
        setLoading(true);
        const token = getCookie('authToken');
        const baseUrl = process.env.REACT_APP_API_URL || '';
        // Optional: include headers if your post request requires authorization
        const response = await axios.get(
          `${baseUrl}/api/offers/${offerId}`,
          {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          }
        );

        const data = response.data?.data || response.data;

        if (data) {
          setOffer(data);

          // Populate modal state with dynamic events from backend
          if (Array.isArray(data.events)) {
            const parsedEvents = data.events.map((ev) => {
              const userPayout = Number(ev.custom_user_amount ?? ev.user_amount ?? 0);
              const limit = Number(ev.amount || ev.payout_limit || ev.limit || (ev.user_amount + ev.refer_amount));
              const currentProfit = Number(ev.profit ?? (limit - userPayout));

              return {
                id: String(ev.event_id || ev.id),
                title: ev.event_name || ev.title || ev.name || 'Event Step',
                userPayout,
                limit,
                profit: currentProfit > 0 ? currentProfit : 0
              };
            });
            setEvents(parsedEvents);
          }
        }
      } catch (error) {
        console.error('Error fetching offer details:', error);
        toast.error(error.response?.data?.msg || "An unexpected error occurred.");
        if (error.response && error.response.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOfferDetails();
  }, [offerId, navigate]);

  const handleCopyLink = (text, type) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    if (type === 'tracking') {
      setCopiedTracking(true);
      setTimeout(() => setCopiedTracking(false), 2000);
    }
  };

  const handlePayoutChange = (id, field, value) => {
    // Sanitize input: allow only digits (blocks '-', '+', 'e', etc.)
    const sanitizedVal = String(value).replace(/[^0-9]/g, '');
    let numericVal = Number(sanitizedVal) || 0;

    setEvents(prev =>
      prev.map(ev => {
        if (ev.id === id) {
          let newUserPayout = ev.userPayout;

          // Prevent individual values from exceeding the total event limit or going below 0
          numericVal = Math.min(ev.limit, Math.max(0, numericVal));

          if (field === 'userPayout') {
            newUserPayout = numericVal;
          }

          const profitBalance = Math.max(0, ev.limit - newUserPayout);

          return {
            ...ev,
            userPayout: newUserPayout,
            profit: profitBalance
          };
        }
        return ev;
      })
    );
  };

  const handleSaveConfiguration = async (e) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      const token = getCookie('authToken');
      const baseUrl = process.env.REACT_APP_API_URL || '';

      // Transform events payload to match backend saveCustomPayoutService expectation
      const payloadEvents = events.map(ev => ({
        event_id: ev.id,
        user_amount: ev.userPayout,
        profit: ev.profit
      }));

      await axios.post(
        `${baseUrl}/api/offers/${offerId}/custom-payout`,
        {
          offerId: offer?.id || offerId,
          events: payloadEvents
        },
        {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        }
      );

      setModalSuccess(true);
      setTimeout(() => {
        setModalSuccess(false);
        setIsPayoutModalOpen(false);
      }, 1500);
    } catch (err) {
      console.error('Failed to save custom payout:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const renderLogo = (logo, name) => {
    const isUrl = typeof logo === 'string' && (logo.startsWith('http') || logo.startsWith('/'));

    if (isUrl) {
      return (
        <div className="w-16 h-16 bg-[#210c12] rounded-2xl border border-rose-950/40 flex items-center justify-center shrink-0 shadow-inner overflow-hidden">
          <img
            src={logo}
            alt={name || 'Offer'}
            loading="lazy"
            className="w-full h-full object-contain p-2"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(name || 'Offer') + '&background=059669&color=fff';
            }}
          />
        </div>
      );
    }

    return (
      <div className="w-16 h-16 bg-zinc-950 rounded-2xl border border-zinc-800 flex items-center justify-center shrink-0 text-3xl shadow-inner">
        {logo || '🎯'}
      </div>
    );
  };

  // Helper arrays synced with backend schema keys
  const stepsList = offer?.steps && Array.isArray(offer.steps) ? offer.steps : (offer?.stepsToEarn || []);
  const tncList = offer?.tnc && Array.isArray(offer.tnc) ? offer.tnc : (offer?.termsAndConditions || []);

  return (
    <div className="w-full pt-4 sm:pt-20 lg:pt-28 pb-16 px-4 sm:px-8 lg:pl-80 relative overflow-hidden font-sans">
      <style>{`
        @keyframes springIn {
          0% { opacity: 0; transform: translateY(12px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
@keyframes runningBorder {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
        @keyframes pulseGlow {
          0%, 100% { opacity: 0.15; transform: scale(1); }
          50% { opacity: 0.35; transform: scale(1.08); }
        }

        @keyframes borderGlow {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        .animate-spring-in {
          animation: springIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .ambient-glow {
          animation: pulseGlow 8s ease-in-out infinite;
        }

        .highlight-border {
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.4) 0%, rgba(24, 24, 27, 0.8) 50%, rgba(20, 184, 166, 0.4) 100%);
          background-size: 200% 200%;
          animation: borderGlow 6s ease infinite;
        }

        .micro-interaction {
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .micro-interaction:hover {
          transform: translateY(-2px);
        }

        .micro-interaction:active {
          transform: translateY(0px) scale(0.98);
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 8px;
        }
          
      `}</style>

      {/* Ambient background blur circles */}
      <div className="absolute top-10 left-1/3 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none ambient-glow" />
      <div className="absolute bottom-10 right-10 w-80 h-80 bg-teal-500/10 rounded-full blur-[100px] pointer-events-none ambient-glow" />

      <div className="max-w-[1200px] mx-auto space-y-6 relative z-10">

        {/* Navigation / Back Button */}
        <div className="flex items-center justify-between animate-spring-in">
          <button
            onClick={() => navigate('/offers')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-900/90 hover:bg-zinc-800 text-zinc-300 hover:text-zinc-100 rounded-xl text-xs font-bold border border-zinc-800 transition-all micro-interaction shadow-lg shadow-black/40"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Offers
          </button>
        </div>

        {/* Content Section */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-zinc-500 text-sm font-semibold tracking-wide">Loading offer details...</p>
          </div>
        ) : !offer ? (
          <div className="text-center py-20 bg-zinc-900/50 rounded-[2.5rem] border border-zinc-800 backdrop-blur-sm animate-spring-in">
            <p className="text-zinc-400 text-base font-semibold">Offer details not found.</p>
          </div>
        ) : (
          <div className="space-y-6 animate-spring-in">

            {/* 1. HIGHLIGHTED OFFER DETAILS SECTION */}
            <div className="relative p-[1px] rounded-[2.5rem] highlight-border shadow-2xl group transition-all duration-500 hover:shadow-emerald-500/10">
              <div className="bg-zinc-900/95 backdrop-blur-xl p-6 sm:p-8 rounded-[2.45rem] relative z-10 space-y-8">

                {/* Top Badge & Header */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                  <div className="flex items-center gap-5">
                    {renderLogo(offer.logo, offer.name)}
                    <div>
                      <div className="flex items-center gap-3">
                        <h1 className="text-2xl sm:text-3xl font-black text-zinc-100 tracking-tight group-hover:text-emerald-400 transition-colors duration-300">
                          {offer.name}
                        </h1>
                        <span className="px-3 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded-lg text-[10px] font-bold uppercase tracking-wider shadow-sm shadow-emerald-500/10 animate-pulse">
                          {offer.category || 'General'}
                        </span>
                      </div>
                      <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider mt-1 flex items-center gap-2">
                        <span>ID: #{offer.id}</span>
                        <span className="w-1 h-1 rounded-full bg-zinc-700"></span>
                        <span className="text-emerald-400">● {offer.status || 'Active'}</span>
                      </p>
                    </div>
                  </div>

                  {/* Standard Payout Display */}
                  <div className="bg-zinc-950/90 border border-zinc-800/80 p-4 rounded-2xl flex flex-col items-start sm:items-end w-full sm:w-auto micro-interaction shadow-inner">
                    <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Default Payout</span>
                    <span className="text-2xl font-black text-emerald-400 mt-0.5">{offer.payout || offer.payout_amount || '₹0'}</span>
                  </div>
                </div>

                {/* Highlighted Stat Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-zinc-950/60 border border-zinc-800/80 hover:border-emerald-500/30 p-4 rounded-2xl micro-interaction transition-all">
                    <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Conversion Rate</p>
                    <p className="text-lg font-black text-zinc-100 mt-1">{offer.conversionRate || offer.conversion_rate || '11.5%'}</p>
                  </div>
                  <div className="bg-zinc-950/60 border border-zinc-800/80 hover:border-emerald-500/30 p-4 rounded-2xl micro-interaction transition-all">
                    <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Traffic Allowed</p>
                    <p className="text-sm font-bold text-zinc-200 mt-1 truncate">{offer.allowedTraffic || offer.allowed_traffic || 'All Channels'}</p>
                  </div>
                  <div className="bg-zinc-950/60 border border-zinc-800/80 hover:border-emerald-500/30 p-4 rounded-2xl micro-interaction transition-all">
                    <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Validation Cycle</p>
                    <p className="text-sm font-bold text-emerald-400 mt-1">{offer.validationCycle || 'Realtime Instant'}</p>
                  </div>
                </div>

                {/* Description */}
                {offer.description && (
                  <div className="pt-6 border-t border-zinc-800/80">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Offer Overview</h3>
                    <p className="text-zinc-300 text-sm leading-relaxed font-medium">
                      {offer.description}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* 2. TRACKING & REFERRAL CONTAINER */}
            <div className="bg-zinc-900/80 backdrop-blur-md p-6 sm:p-8 rounded-[2.5rem] border border-zinc-800/80 shadow-2xl space-y-6 animate-spring-in">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-zinc-800/80 pb-4">
                <div>
                  <h3 className="text-lg font-extrabold text-zinc-100">Tracking &amp; Referral Links</h3>
                  <p className="text-zinc-400 text-xs font-medium mt-0.5">
                    Generate and share your personalized promotion URLs.
                  </p>
                </div>
                <span className="px-3 py-1 bg-zinc-950 text-emerald-400 border border-zinc-800 rounded-xl text-xs font-mono self-start sm:self-auto">
                  Status: Active
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Tracking Link Card */}
                <div className="bg-zinc-950/70 p-5 rounded-2xl border border-zinc-800/80 space-y-3 micro-interaction hover:border-emerald-500/30">
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14.828 14.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      Direct Tracking Link
                    </label>
                  </div>
                  <input
                    type="text"
                    readOnly
                    value={offer.offerUrl || offer.trackingLink || ''}
                    className="w-full px-4 py-2.5 bg-zinc-900/90 border border-zinc-800 rounded-xl text-zinc-200 text-xs font-mono focus:outline-none select-all"
                  />
                  <button
                    onClick={() => handleCopyLink(offer.offerUrl || offer.trackingLink, 'tracking')}
                    className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all micro-interaction active:scale-95 shadow-lg ${copiedTracking
                      ? 'bg-teal-500 text-zinc-950 shadow-teal-500/20'
                      : 'bg-emerald-500 hover:bg-emerald-400 text-zinc-950 shadow-emerald-500/20'
                      }`}
                  >
                    {copiedTracking ? 'Copied to Clipboard!' : 'Copy Tracking Link'}
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>

                {/* Referral Link Card removed as requested */}

              </div>
            </div>

            {/* 3. CUSTOM PAYOUT CONTAINER */}
            <div className="bg-zinc-900/80 backdrop-blur-md p-6 sm:p-8 rounded-[2.5rem] border border-zinc-800/80 shadow-2xl animate-spring-in flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="space-y-1 text-center md:text-left">
                <span className="px-3 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg text-[10px] font-bold uppercase tracking-wider">
                  Volume Booster
                </span>
                <h3 className="text-xl font-black text-zinc-100 tracking-tight pt-1">Request Custom Payout</h3>
                <p className="text-zinc-400 text-xs font-medium max-w-xl">
                  Driving 50+ conversions daily? You qualify for bumped payouts and custom commercial terms.
                </p>
                {/* Note with running gradient border and micro-animation */}
                <div className="relative mt-3 p-[1.5px] rounded-xl overflow-hidden">
                  {/* Running gradient border layer */}
                  <div
                    className="absolute inset-0 rounded-xl opacity-70"
                    style={{
                      background: 'linear-gradient(90deg, transparent 0%, rgba(16,185,129,0.9) 25%, rgba(20,184,166,0.9) 50%, rgba(16,185,129,0.9) 75%, transparent 100%)',
                      backgroundSize: '200% 100%',
                      animation: 'runningBorder 3s linear infinite',
                    }}
                  />
                  <div className="relative flex items-start gap-2.5 p-3 bg-zinc-950 rounded-[10px] transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/10 group">
                    <div className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full border border-emerald-500/40 flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12">
                      <svg className="w-3 h-3 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-[11px] font-medium text-zinc-300 leading-relaxed">
                      <span className="text-emerald-400 font-bold">Note:</span> When a user completes an offer through your link, they receive cashback directly to their payment method, while the profit amount is credited to your wallet — available for withdrawal at any time.
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setIsPayoutModalOpen(true)}
                className="px-6 py-3.5 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-xl text-xs font-black shadow-lg shadow-emerald-500/20 transition-all micro-interaction active:scale-95 flex-shrink-0"
              >
                Apply for Higher Payout
              </button>
            </div>

            {/* 4. HOW TO EARN SECTION (SEPARATE CONTAINER BELOW CUSTOM PAYOUT) */}
            {stepsList.length > 0 && (
              <div className="bg-zinc-900/80 backdrop-blur-md p-6 sm:p-8 rounded-[2.5rem] border border-zinc-800/80 shadow-2xl animate-spring-in space-y-6">
                <h3 className="text-lg font-extrabold text-zinc-100 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
                  How to Earn
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {stepsList.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-3.5 p-4 bg-zinc-950/80 rounded-2xl border border-zinc-800/80 hover:border-emerald-500/20 micro-interaction transition-all">
                      <span className="flex-shrink-0 w-8 h-8 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-bold text-xs flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <p className="text-zinc-300 text-xs font-medium pt-1.5 leading-relaxed">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 5. TERMS & CONDITIONS SECTION */}
            {tncList.length > 0 && (
              <div className="bg-zinc-900/80 backdrop-blur-md border border-zinc-800/80 rounded-[2.5rem] p-6 sm:p-8 shadow-2xl animate-spring-in">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2.5 bg-rose-500/10 text-rose-400 border border-rose-500/20 rounded-xl">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-black tracking-tight text-zinc-100">Terms &amp; Conditions</h3>
                </div>

                <div className="space-y-3">
                  {tncList.map((term, idx) => (
                    <div key={idx} className="flex items-start gap-3 micro-interaction">
                      <div className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-2 shrink-0"></div>
                      <p className="text-sm font-medium text-zinc-400 leading-relaxed">{term}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        )}

        {/* CONFIGURE EVENT PAYOUTS MODAL */}
        {isPayoutModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-spring-in">
            <div className="relative w-full max-w-2xl bg-zinc-900 border border-zinc-800 rounded-[2.5rem] shadow-2xl overflow-hidden">
              <div className="p-6 sm:p-8">

                {/* Modal Header */}
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-xl font-black tracking-tight text-zinc-100">Configure Event Payouts</h3>
                    <p className="text-xs text-zinc-400 font-bold uppercase tracking-widest mt-0.5">{offer?.name}</p>
                  </div>
                  <button
                    onClick={() => setIsPayoutModalOpen(false)}
                    className="p-2 bg-zinc-950 border border-zinc-800 text-zinc-400 hover:text-rose-400 rounded-xl transition-colors micro-interaction"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {modalSuccess ? (
                  <div className="py-12 text-center space-y-3 animate-spring-in">
                    <div className="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center mx-auto text-xl">
                      ✓
                    </div>
                    <h4 className="text-lg font-bold text-zinc-100">Configuration Saved!</h4>
                    <p className="text-xs text-zinc-400">Event payout settings updated successfully.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSaveConfiguration}>
                    <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-2 custom-scrollbar">
                      {events.map((ev) => {
                        return (
                          <div key={ev.id} className="p-5 bg-zinc-950 rounded-[2rem] border border-zinc-800/80 space-y-4">
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                                <h4 className="font-bold text-zinc-100">{ev.title}</h4>
                              </div>
                              <div className="flex items-center gap-1.5 px-3 py-1 bg-amber-500/10 text-amber-400 rounded-full border border-amber-500/20">
                                <span className="text-[10px] font-black uppercase">Your Profit:</span>
                                <span className="text-xs font-bold">₹{ev.profit}</span>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div className="space-y-1.5">
                                <div className="flex justify-between items-end px-1">
                                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-400">User Payout</label>
                                  <span className="text-[9px] font-bold text-zinc-500">Limit: ₹{ev.limit}</span>
                                </div>
                                <div className="relative">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-400 font-bold text-sm">₹</span>
                                  <input
                                    className="w-full pl-8 pr-3 py-3 bg-zinc-900 border border-zinc-800 rounded-xl focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/10 font-bold text-sm text-zinc-100"
                                    type="number"
                                    min="0"
                                    max={ev.limit}
                                    value={ev.userPayout}
                                    onKeyDown={(e) => {
                                      if (['-', '+', 'e', 'E'].includes(e.key)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    onChange={(e) => handlePayoutChange(ev.id, 'userPayout', e.target.value)}
                                  />
                                </div>
                              </div>

                              {/* Refer Payout field removed as requested */}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-6 space-y-4">
                      <div className="flex items-start gap-2.5 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl text-emerald-400">
                        <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-[10px] font-medium text-zinc-300">
                          The payout cannot exceed the total event value. Your margin is calculated in real-time above.
                        </p>
                      </div>

                      <button
                        type="submit"
                        disabled={isSaving}
                        className="w-full flex items-center justify-center gap-2 py-4 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-2xl font-bold transition-all micro-interaction active:scale-95 shadow-xl shadow-emerald-500/20 disabled:opacity-50"
                      >
                        {isSaving ? 'Saving...' : 'Save Configuration'}
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                        </svg>
                      </button>
                    </div>
                  </form>
                )}

              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default OfferDetailsPage;